import { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Chip, IconButton, InputAdornment, LinearProgress, MenuItem, Pagination, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const PAGE_SIZE = 10;

export default function MobileSituationList({
  rows, summary, keyword, onKeywordChange, filters, onFiltersChange, categories, causes,
  source, onSourceChange, selectedIds, onSelectionChange, selectionMode, onSelectionModeChange,
  onAdd, onEdit, onCopy, onConnect, onDelete, loading, error, onRetry,
}) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  useEffect(() => { setPage(1); }, [keyword, filters.danger_category, filters.danger_cause, source]);
  const resetFilters = () => {
    onKeywordChange(""); onFiltersChange({ danger_category: "", danger_cause: "" }); onSourceChange("all");
  };
  return <Stack spacing={1.25} sx={{ minWidth: 0, "& .MuiButton-root": { minHeight: "44px !important", boxShadow: "none" }, "& .MuiOutlinedInput-root": { minHeight: 44 } }}>
    <ToggleButtonGroup exclusive fullWidth value={source} onChange={(_, value) => value && onSourceChange(value)}
      aria-label="위험요인 출처" sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { minHeight: 44, gap: 0.75, px: 0.5, fontSize: 13 } }}>
      <ToggleButton value="all">전체 <strong>{summary.total}</strong></ToggleButton>
      <ToggleButton value="manual">일반 등록 <strong>{summary.manual}</strong></ToggleButton>
      <ToggleButton value="worker">근로자 제안 <strong>{summary.worker}</strong></ToggleButton>
    </ToggleButtonGroup>
    <TextField fullWidth size="small" label="검색" placeholder="위험분류, 위험요인, 발생상황 검색"
      value={keyword} onChange={(event) => onKeywordChange(event.target.value)}
      sx={{ bgcolor: "background.paper" }} InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        endAdornment: keyword ? <InputAdornment position="end"><IconButton aria-label="검색어 지우기" onClick={() => onKeywordChange("")}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
      }} />
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
      <TextField select fullWidth size="small" label="위험분류" value={filters.danger_category}
        onChange={(event) => onFiltersChange({ danger_category: event.target.value, danger_cause: "" })}
        SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ bgcolor: "background.paper" }}>
        <MenuItem value="">전체</MenuItem>
        {categories.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <TextField select fullWidth size="small" label="위험요인" value={filters.danger_cause}
        onChange={(event) => onFiltersChange({ ...filters, danger_cause: event.target.value })}
        SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} sx={{ bgcolor: "background.paper" }}>
        <MenuItem value="">전체</MenuItem>
        {causes.map((value) => <MenuItem key={value} value={value} sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{value}</MenuItem>)}
      </TextField>
    </Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{rows.length}건</Typography>
      <Stack direction="row" spacing={0.5}>
        <Button size="small" color={selectionMode ? "inherit" : "error"}
          startIcon={selectionMode ? <CloseIcon /> : <DeleteOutlineIcon />}
          onClick={() => { onSelectionModeChange(!selectionMode); onSelectionChange([]); }}>
          {selectionMode ? "선택 취소" : "선택 삭제"}
        </Button>
        {selectionMode ? <Button size="small" variant="contained" color="error" disabled={!selectedIds.length} onClick={onDelete}>
          {selectedIds.length}건 삭제
        </Button> : <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onAdd}>추가</Button>}
      </Stack>
    </Stack>
    {loading && <LinearProgress aria-label="위험요인 조회 중" />}
    {error ? <Alert severity="error" action={<Button onClick={onRetry}>재시도</Button>}>위험요인을 불러오지 못했습니다.</Alert> :
      !loading && rows.length === 0 ? <Box sx={{ py: 5, textAlign: "center" }}>
        <Typography color="text.secondary">검색 결과가 없습니다.</Typography>
        <Button onClick={resetFilters} sx={{ mt: 1 }}>필터 초기화</Button>
      </Box> : rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((row) => {
        const selected = selectedIds.includes(row.danger_cause_id);
        const worker = Number(row.worker_proposal_source) === 1;
        return <Box component="article" key={row.danger_cause_id} aria-label={row.danger_description || "위험요인"}
          sx={{ p: 1.5, bgcolor: "background.paper", border: "1px solid", borderColor: selected ? "primary.main" : "divider", borderRadius: 2, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Chip size="small" variant="outlined" color={worker ? "warning" : "default"} label={worker ? "근로자 제안" : "일반 등록"} sx={{ height: 24, borderRadius: 1 }} />
            {selectionMode && <Checkbox checked={selected} size="small"
              inputProps={{ "aria-label": `${row.danger_description || "위험요인"} 선택` }}
              onChange={() => onSelectionChange(selected ? selectedIds.filter((id) => id !== row.danger_cause_id) : [...selectedIds, row.danger_cause_id])} />}
          </Stack>
          <Typography sx={{ my: 1, fontSize: 14, lineHeight: 1.65, overflowWrap: "anywhere" }}>{row.danger_description || "내용 미등록"}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
            {[row.danger_category, row.danger_cause].filter(Boolean).join(" · ") || "분류 미지정"}
          </Typography>
          {worker && row.latest_worker_proposal_text && <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">제안 #{row.latest_worker_proposal_id || "-"} · {row.latest_worker_proposer_name || "제안자 미확인"}</Typography>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{row.latest_worker_proposal_text}</Typography>
          </Box>}
          {!selectionMode && <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1fr", mt: 1.25, pt: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Button size="small" color="inherit" startIcon={<EditOutlinedIcon />} onClick={() => onEdit(row)}>수정</Button>
            <Button size="small" color="inherit" startIcon={<ContentCopyIcon />} onClick={() => onCopy(row)}>사본 추가</Button>
            <Button size="small" startIcon={<LinkIcon />} onClick={() => onConnect(row)}>연결</Button>
          </Box>}
        </Box>;
      })}
    {pageCount > 1 && <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
      <Pagination count={pageCount} page={currentPage} onChange={(_, value) => { setPage(value); onSelectionChange([]); }}
        siblingCount={0} boundaryCount={1} size="small" color="primary" aria-label="위험요인 페이지"
        sx={{ "& .MuiPaginationItem-root": { minWidth: 40, height: 44, mx: 0.25 } }} />
    </Box>}
  </Stack>;
}
